package dev.lingvo.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import dev.lingvo.shared.validateEmail
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.components.marketing.AuthField
import dev.lingvo.site.components.marketing.AuthPageScaffold
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.Space
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.dom.Form
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

/**
 * Asking for a password-reset link.
 *
 * The missing half of a flow that was otherwise complete: the API endpoint, the email template and
 * [ResetPasswordPage] — where the emailed link lands — all existed, but nothing in the product ever
 * asked for the email to be sent. A user who forgot their password was simply locked out.
 *
 * **The result is the same whether or not the address has an account.** Different answers would turn
 * this form into an account-enumeration oracle: anyone could learn which of a list of addresses are
 * customers. The server already answers uniformly; this page must not undo that by, say, showing an
 * error only for unknown addresses.
 */
@Page("/forgot-password")
@Composable
fun ForgotPasswordPage() {
    val scope = rememberCoroutineScope()

    var email by remember { mutableStateOf("") }
    var submitted by remember { mutableStateOf(false) }
    var sending by remember { mutableStateOf(false) }
    var sent by remember { mutableStateOf(false) }
    var apiError by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) { document.title = tr("forgot_password.reset_your_password_lingvo", "Reset your password — Lingvo") }

    val emailError = if (submitted) validateEmail(email.trim()) else null

    fun submit() {
        submitted = true
        apiError = null
        if (validateEmail(email.trim()) != null || sending) return
        sending = true
        scope.launch {
            try {
                ApiClient.requestPasswordReset(email.trim())
                sent = true
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                // Only genuine transport or server failures land here. An unknown address is a
                // success as far as this page is concerned, by design.
                apiError = failure.message?.takeIf(String::isNotBlank)
                    ?: tr(
                        "forgot_password.couldnt_send_the_link_check_your",
                        "Couldn't send the link. Check your connection and retry.",
                    )
            } finally {
                sending = false
            }
        }
    }

    AuthPageScaffold(
        title = if (sent) {
            t("forgot_password.check_your_inbox", "Check your inbox")
        } else {
            t("forgot_password.reset_your_password", "Reset your password")
        },
        subtitle = if (sent) {
            t(
                "forgot_password.if_that_address_has_a_lingvo",
                "If that address has a Lingvo account, a link to choose a new password is on its way.",
            )
        } else {
            t(
                "forgot_password.enter_the_address_you_signed_up",
                "Enter the address you signed up with and we'll send you a link.",
            )
        },
        alternatePrompt = t("forgot_password.remembered_it", "Remembered it?"),
        alternateAction = t("forgot_password.back_to_log_in", "Back to log in"),
        alternatePath = "/login",
    ) {
        if (sent) {
            Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                LAlert(
                    t("forgot_password.the_link_expires_in_2_hours", "The link expires in 2 hours"),
                    t(
                        "forgot_password.it_only_works_once_if_it",
                        "It only works once. If it does not arrive within a few minutes, check spam — " +
                            "and you can request another from this page.",
                    ),
                    variant = LAlertVariant.INFO,
                    // The whole panel replaced the form because the reader pressed "Send reset
                    // link", and the focused button went with it. Polite, since INFO is a `status`
                    // region — but without it the answer to what they just did is announced by
                    // nothing at all.
                    live = true,
                )
                LButton(
                    label = t("forgot_password.send_another_link", "Send another link"),
                    onClick = { sent = false },
                    modifier = Modifier.fillMaxWidth(),
                    variant = LButtonVariant.GHOST,
                )
            }
            return@AuthPageScaffold
        }

        Form(
            attrs = Modifier.fillMaxWidth().toAttrs {
                attr("novalidate", "")
                onSubmit { event ->
                    event.preventDefault()
                    submit()
                }
            },
        ) {
            Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                apiError?.let { message ->
                    LAlert(
                        t("forgot_password.couldnt_send_the_link", "Couldn't send the link"),
                        message,
                        variant = LAlertVariant.DANGER,
                        // It appeared because the reader just pressed "Send reset link".
                        live = true,
                    )
                }
                AuthField(
                    value = email,
                    onValueChange = { email = it; apiError = null },
                    label = t("forgot_password.email", "Email"),
                    placeholderText = "you@company.com",
                    inputType = InputType.Email,
                    name = "email",
                    autocomplete = "email",
                    error = emailError,
                    enabled = !sending,
                    onBlur = {},
                    onCommit = ::submit,
                )
                LButton(
                    label = t("forgot_password.send_reset_link", "Send reset link"),
                    onClick = ::submit,
                    modifier = Modifier.fillMaxWidth(),
                    enabled = !sending,
                    loading = sending,
                )
                // Said before the reset, not only after it. `/auth/password-reset/confirm` revokes
                // every API token, every session and every agent connection this person created, in
                // the same transaction as the password change — so for a product whose argument is
                // that the CLI drives it and it runs in CI, a forgotten password quietly breaks the
                // pipeline. Somebody who learns that here can schedule the reset and reissue the
                // token; somebody who learns it from a red build cannot.
                LAlert(
                    t("forgot_password.finishing_a_reset_signs_you_out", "Finishing a reset signs you out everywhere"),
                    t(
                        "forgot_password.changing_the_password_revokes_every_api",
                        "Changing the password revokes every API token and agent connection you " +
                            "created and signs out every device, so the CLI and CI will need a new " +
                            "token afterwards.",
                    ),
                    variant = LAlertVariant.WARNING,
                    // Not live. It is on the page at first paint and nothing the reader did put it
                    // there, which is exactly the case LAlert's default is for.
                )
            }
        }
    }
}
